import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import environment from '../config/environment';
import { storage } from '../utils/helpers';

// API Response types matching Django backend
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

export interface ApiError {
  success: false;
  error: string;
  details?: any;
}

// Token management
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

// DRF authenticates before it checks permissions, so a token left over from a
// deleted user turns these AllowAny endpoints into a 401 the visitor cannot
// clear — locked out of signing up or signing back in.
const ANONYMOUS_ENDPOINTS = [
  '/auth/register/',
  '/auth/login/',
  '/auth/reset-password/',
  '/auth/reset-password/confirm/',
  // The refresh call must reject straight through: the retry queue it would
  // otherwise join is only flushed once the refresh itself settles.
  '/auth/auth/refresh/',
];

const isAnonymousEndpoint = (url?: string) =>
  !!url && ANONYMOUS_ENDPOINTS.some((path) => url.startsWith(path));

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: environment.API_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token and current page header
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token && !isAnonymousEndpoint(config.url)) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Send current page path for Prometheus page tracking
        if (typeof window !== 'undefined') {
          config.headers['X-Current-Page'] = window.location.pathname;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // A 401 from login or register means bad credentials, not a stale
        // session: refreshing and replaying it would only hide the real error.
        if (isAnonymousEndpoint(originalRequest?.url)) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue the request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              return this.client(originalRequest);
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) {
              this.clearTokens();
              return Promise.reject(error);
            }

            const response = await this.refreshToken(refreshToken);
            this.setTokens(response.data.access, response.data.refresh);
            
            // Process failed queue
            this.processQueue(null);
            
            // Retry original request
            return this.client(originalRequest);
          } catch (refreshError) {
            this.processQueue(refreshError);
            this.clearTokens();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
    
    this.failedQueue = [];
  }

  // Token management methods
  private getAccessToken(): string | null {
    return storage.get<string>(TOKEN_KEY);
  }

  private getRefreshToken(): string | null {
    return storage.get<string>(REFRESH_TOKEN_KEY);
  }

  private setTokens(accessToken: string, refreshToken: string) {
    storage.set(TOKEN_KEY, accessToken);
    storage.set(REFRESH_TOKEN_KEY, refreshToken);
  }

  private clearTokens() {
    storage.remove(TOKEN_KEY);
    storage.remove(REFRESH_TOKEN_KEY);
    storage.remove(USER_KEY);
  }

  // Authentication methods
  async login(identifier: string, password: string): Promise<ApiResponse> {
    const response = await this.client.post('/auth/login/', {
      identifier, // Backend now accepts 'identifier' field
      password,
    });
    
    const { tokens, user } = response.data;
    this.setTokens(tokens.access, tokens.refresh);
    storage.set(USER_KEY, user);
    
    return {
      success: true,
      data: { user, access: tokens.access, refresh: tokens.refresh },
      message: 'Login successful',
    };
  }

  async register(userData: {
    display_name: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
  }): Promise<ApiResponse> {
    // Step 1: Register the user
    const registerResponse = await this.client.post('/auth/register/', userData);
    
    if (!registerResponse.data.user) {
      throw new Error('Registration failed - no user returned');
    }
    
    // Step 2: Automatically log in the user to get tokens
    // TODO: Remove auto-login before production - users should confirm email first
    // This is temporary for development/testing purposes only
    const loginResponse = await this.client.post('/auth/login/', {
      identifier: userData.display_name,
      password: userData.password,
    });
    
    const { tokens, user } = loginResponse.data;
    this.setTokens(tokens.access, tokens.refresh);
    storage.set(USER_KEY, user);
    
    return {
      success: true,
      data: { user, access: tokens.access, refresh: tokens.refresh },
      message: 'Registration and login successful',
    };
  }

  async logout(): Promise<ApiResponse> {
    try {
      await this.client.post('/auth/logout/');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    }
    
    this.clearTokens();
    
    return {
      success: true,
      data: null,
      message: 'Logout successful',
    };
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/change-password/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPassword,
    });
  }

  async exportAccount(): Promise<Blob> {
    const response = await this.client.get('/auth/export-account/', { responseType: 'blob' });
    return response.data as Blob;
  }

  async deleteAccount(password: string): Promise<void> {
    await this.client.post('/auth/delete-account/', { password });
    this.clearTokens();
  }

  async refreshToken(refreshToken: string): Promise<AxiosResponse> {
    return this.client.post('/auth/auth/refresh/', {
      refresh: refreshToken,
    });
  }

  // User management methods
  async getCurrentUser(): Promise<ApiResponse> {
    const response = await this.client.get('/users/profile/');
    // Django backend already returns structured response, don't wrap it again
    return response.data;
  }

  async updateProfile(userData: Partial<{
    first_name: string;
    last_name: string;
    email: string;
    display_name: string;
  }>): Promise<ApiResponse> {
    const response = await this.client.patch('/users/profile/', userData);
    
    // Update stored user data
    const currentUser = storage.get(USER_KEY);
    if (currentUser && response.data.data) {
      storage.set(USER_KEY, { ...currentUser, ...response.data.data });
    }
    
    // Django backend already returns structured response, don't wrap it again
    return response.data;
  }

  // Generic API methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get(url, config);
    return {
      success: true,
      data: response.data,
      message: 'Request successful',
    };
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, data, config);
    return {
      success: true,
      data: response.data,
      message: 'Request successful',
    };
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put(url, data, config);
    return {
      success: true,
      data: response.data,
      message: 'Request successful',
    };
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch(url, data, config);
    return {
      success: true,
      data: response.data,
      message: 'Request successful',
    };
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete(url, config);
    return {
      success: true,
      data: response.data,
      message: 'Request successful',
    };
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  getStoredUser() {
    return storage.get(USER_KEY);
  }

  // Error handling
  handleError(error: any): ApiError {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data;
      // Extract error message from various Django response formats
      let errorMessage = 'Sunucu hatası';
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.error) {
        errorMessage = data.error;
      } else if (data?.detail) {
        errorMessage = data.detail;
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (data?.non_field_errors) {
        errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
      } else if (typeof data === 'object' && data !== null) {
        // Try to extract first field error
        const firstKey = Object.keys(data)[0];
        if (firstKey) {
          const val = data[firstKey];
          errorMessage = `${firstKey}: ${Array.isArray(val) ? val[0] : val}`;
        }
      }
      return {
        success: false,
        error: errorMessage,
        details: data,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        error: 'Network error - please check your connection',
        details: error.request,
      };
    } else {
      // Something else happened
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
        details: error,
      };
    }
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();
export default apiClient;
